import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';
import { inSkipList } from '.';

@Injectable()
export class CacheInterceptor implements HttpInterceptor {
  private cache = new Map<string, { expiry: number; response: HttpResponse<any> }>();
  private inProgressRequests = new Map<string, Observable<HttpEvent<any>>>();

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // If the request method is not GET, we do not cache the response
    if (req.method !== 'GET') {
      // For modifying requests, clear the cache
      if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(req.method)) {
        this.cache.clear();
      }

      return next.handle(req);
    }

    if (inSkipList(req.url)) {
      return next.handle(req);
    }

    const cacheKey = this.createCacheKey(req);

    const cachedResponse = this.cache.get(cacheKey);

    // Check if the cached response exists and is still valid
    if (cachedResponse && Date.now() < cachedResponse.expiry) {
      return of(cachedResponse.response);
    }

    // Check if the request is already in progress
    if (this.inProgressRequests.has(cacheKey)) {
      return this.inProgressRequests.get(cacheKey)!;
    }

    // Make the request and cache the response
    const request = next.handle(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          // Cache for 5 seconds
          this.cache.set(cacheKey, { response: event, expiry: Date.now() + 5000 });
        }
      }),
      shareReplay(1),
      finalize(() => {
        this.inProgressRequests.delete(cacheKey);
      }),
    );

    // Store the in-progress request
    this.inProgressRequests.set(cacheKey, request);

    return request;
  }

  private createCacheKey(req: HttpRequest<any>): string {
    // Create a unique key using the URL and the serialized body
    const body = req.body ? JSON.stringify(req.body) : '';
    return `${req.urlWithParams}|${body}`;
  }
}
